const OVERPASS_ENDPOINTS = [
  "https://overpass.openstreetmap.fr/api/interpreter",
  "https://overpass-api.de/api/interpreter",
  "https://overpass.kumi.systems/api/interpreter",
];

const OVERPASS_TIMEOUT_MS = 10000;

async function overpassFetch(query) {
  const failures = [];
  for (const url of OVERPASS_ENDPOINTS) {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), OVERPASS_TIMEOUT_MS);
    try {
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "application/json",
          // Public Overpass mirrors reject generic/bot-looking User-Agents
          // (e.g. React Native's default "okhttp/..." on Android) — Overpass's
          // own usage policy asks clients to identify themselves. Browsers
          // forbid scripts from overriding this header, so this is a no-op
          // on web but matters on mobile.
          "User-Agent": "AndiamoPlaces/1.0 (https://andiamoplaces.com)",
        },
        body: `data=${encodeURIComponent(query)}`,
        signal: controller.signal,
      });
      if (!resp.ok) {
        failures.push(`${url}: HTTP ${resp.status}`);
        continue;
      }
      const data = await resp.json();
      return data;
    } catch (e) {
      failures.push(`${url}: ${e.message || e}`);
    } finally {
      clearTimeout(timer);
    }
  }
  console.error("[OverpassTrails] all endpoints failed:", failures);
  throw new Error(`All Overpass API servers are unreachable. Try again later.\n${failures.join("\n")}`);
}

function speedForRoute(routeType) {
  if (!routeType) return 4;
  const t = routeType.toLowerCase();
  if (t.includes("mtb")) return 10;
  if (t.includes("bicycle")) return 15;
  if (t.includes("hiking")) return 3;
  return 4;
}

export function estimateDurationHours(distanceKm, routeType) {
  if (!distanceKm || distanceKm <= 0) return null;
  return distanceKm / speedForRoute(routeType);
}

// Fetch metadata for all route types — filtering is done client-side
export async function searchTrails(bounds) {
  const { south, west, north, east } = bounds;
  const query =
    `[out:json][timeout:25];\n` +
    `relation["type"="route"]["route"~"foot|walking|hiking|bicycle|mtb"]["name"]` +
    `(${south},${west},${north},${east});\n` +
    `out tags 100;`;

  console.log("[OverpassTrails] searchTrails query:", query);

  let data;
  try {
    data = await overpassFetch(query);
  } catch (e) {
    throw new Error(e.message || "Network error contacting Overpass API.");
  }

  console.log("[OverpassTrails] searchTrails result:", data.elements?.length, "elements", data.remark || "");

  if (data.remark && !(data.elements || []).length)
    throw new Error("Query timed out — try zooming in closer.");

  return parseTags(data.elements || []);
}

// Fetch geometry for a single relation — pulls member ways with inline geometry
export async function fetchTrailGeometry(id) {
  // way(r) selects member ways of the relation directly; out geom adds inline coordinates
  const query =
    `[out:json][timeout:45];\n` +
    `relation(${id});\n` +
    `way(r);\n` +
    `out geom;`;

  console.log("[OverpassTrails] fetchTrailGeometry", id);

  let data;
  try {
    data = await overpassFetch(query);
  } catch (e) {
    console.error("[OverpassTrails] fetchTrailGeometry error", e.message);
    return null;
  }

  const elements = data.elements || [];
  console.log("[OverpassTrails] fetchTrailGeometry elements:", elements.length, data.remark || "");
  const result = parseGeometry(elements);
  console.log("[OverpassTrails] fetchTrailGeometry segments:", result ? result.segments.length : "null");
  return result;
}

function parseTags(elements) {
  return elements
    .filter((el) => el.type === "relation" && el.tags)
    .map((el) => {
      const tags = el.tags;
      const distanceKm = parseFloat(tags.distance || tags.length) || null;
      const routeType = tags.route || "foot";
      return {
        id: el.id,
        name: tags["name:en"] || tags.name || `Route ${el.id}`,
        routeType,
        distanceKm,
        durationHours: estimateDurationHours(distanceKm, routeType),
        description: tags["description:en"] || tags.description || tags.note || "",
        wikipedia: tags.wikipedia || "",
        network:   tags.network  || "",
        roundtrip: tags.roundtrip || "",
        operator:  tags.operator || "",
        website:   tags.website || tags.url || "",
      };
    });
}

// Returns display info for notable network levels; returns null for local/unknown.
export function networkInfo(network) {
  if (!network) return null;
  const n = network.toLowerCase();
  if (n === "iwn" || n === "icn" || n === "international")
    return { label: "International", color: "#6f42c1" };
  if (n === "nwn" || n === "ncn" || n === "national")
    return { label: "National", color: "#0d6efd" };
  if (n === "rwn" || n === "rcn" || n === "regional")
    return { label: "Regional", color: "#fd7e14" };
  return null;
}

// Extract way geometries from the Overpass response
function parseGeometry(elements) {
  const nodeCoords = {};
  for (const el of elements) {
    if (el.type === "node" && el.lat != null && el.lon != null)
      nodeCoords[el.id] = { lat: el.lat, lon: el.lon };
  }

  const segments = [];
  for (const el of elements) {
    if (el.type !== "way") continue;
    let pts;
    if (el.geometry && el.geometry.length >= 2) {
      pts = el.geometry
        .filter((g) => g.lat != null && g.lon != null)
        .map((g) => ({ lat: g.lat, lon: g.lon }));
    } else if (el.nodes && el.nodes.length >= 2) {
      pts = el.nodes.map((nid) => nodeCoords[nid]).filter(Boolean);
    }
    if (pts && pts.length >= 2) segments.push(pts);
  }

  const firstPt = segments.length > 0 ? segments[0][0] : null;
  return segments.length > 0 ? { segments, firstPt } : null;
}

// Bidirectional greedy chain: at each step, pick the unvisited segment whose
// nearest endpoint is closest to EITHER end of the current chain, growing from
// both head and tail. This avoids the "crossing line" the single-direction
// greedy produces when the starting segment is in the middle of the route.
function chainSegments(segments) {
  if (segments.length === 0) return [];
  const rem = segments.map((s) => [...s]);
  const chain = rem.splice(0, 1)[0];

  while (rem.length > 0) {
    const head = chain[0];
    const tail = chain[chain.length - 1];
    let bestIdx = 0, bestAtHead = false, bestReverse = false, bestDist = Infinity;

    for (let i = 0; i < rem.length; i++) {
      const seg = rem[i];
      const s0 = seg[0], se = seg[seg.length - 1];

      // Candidate: append to tail
      const dTS = sqDist(tail, s0);
      const dTE = sqDist(tail, se);
      // Candidate: prepend to head (after reversal, seg[last] touches head)
      const dHE = sqDist(head, se);   // se touches head → prepend as-is
      const dHS = sqDist(head, s0);   // s0 touches head → prepend reversed

      if (dTS < bestDist) { bestDist = dTS; bestIdx = i; bestAtHead = false; bestReverse = false; }
      if (dTE < bestDist) { bestDist = dTE; bestIdx = i; bestAtHead = false; bestReverse = true;  }
      if (dHE < bestDist) { bestDist = dHE; bestIdx = i; bestAtHead = true;  bestReverse = false; }
      if (dHS < bestDist) { bestDist = dHS; bestIdx = i; bestAtHead = true;  bestReverse = true;  }
    }

    const next = rem.splice(bestIdx, 1)[0];
    if (bestReverse) next.reverse();
    const skip = bestDist < 1e-12; // connected OSM ways share exact endpoints

    if (!bestAtHead) {
      // append: next[0] touches tail
      chain.push(...(skip ? next.slice(1) : next));
    } else {
      // prepend: next[last] touches head
      chain.unshift(...(skip ? next.slice(0, -1) : next));
    }
  }

  return chain;
}

function sqDist(a, b) {
  const dlat = a.lat - b.lat;
  const dlon = a.lon - b.lon;
  return dlat * dlat + dlon * dlon;
}

// Produces the flat lon,lat:lon,lat:... format that Place.getPolyline parses.
export function segmentsToPoints(segments) {
  return chainSegments(segments)
    .map((p) => `${p.lon},${p.lat}`)
    .join(":");
}

// Returns each segment as its own {lat,lng}[] array — used for preview Polylines
// so we draw one line per segment and avoid "teleport" lines between gaps.
export function segmentsToLatLng(segments) {
  return segments.map((seg) => seg.map((p) => ({ lat: p.lat, lng: p.lon })));
}

export function uniqueGroupName(desiredName, existingNames) {
  if (!existingNames.includes(desiredName)) return desiredName;
  let i = 2;
  while (existingNames.includes(`${desiredName} ${i}`)) i++;
  return `${desiredName} ${i}`;
}

export function formatDuration(hours) {
  if (hours == null) return null;
  const h = Math.floor(hours);
  const m = Math.round((hours - h) * 60);
  if (h === 0) return `${m}m`;
  if (m === 0) return `${h}h`;
  return `${h}h ${m}m`;
}
